/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */
import { useServiceContext } from '../root'
import { WorkflowStatus, TASK_ACTIONS } from '../constants/workflow'
import _ from 'lodash'
import { getWorkflowFiles } from '~/services/api/workflows'
import { toast } from '~/services/toast'
import { useNavigate } from 'react-router'

const QC_DOC_STATUS = {
  PASSED: 'PASSED',
  FAILED: 'FAILED',
  REMEDIATED: 'REMEDIATED',
  NA: 'NA'
}

interface Task {
  id?: number
  assignee?: {
    id: number
  }
  workflow?: {
    id: number
  }
  status?: string
}

interface WorkflowInfo {
  status?: string
  initiatorUserId?: number
  workflowSteps?: Array<{
    status?: string
    taskTeamSteps?: Array<{
      taskTeamItems?: Array<{
        userId?: number
      }>
    }>
  }>
}

interface WorkflowFilesResponse {
  elements?: Array<{
    draftFsi?: {
      id: number
    }
    sourceFsi?: {
      id: number
      versionId?: number
    }
  }>
}

interface TasksResponse {
  elements?: Array<{
    id: number
    status?: string
    assignee?: {
      id: number
    }
  }>
}

interface CheckAccessRedirectionParams {
  wfStatus: string | undefined
  currentLoggedInUserId: number | null
  currentTaskAssigneeIds: (number | undefined)[]
  wfInitiatorId: number | undefined
  wfId: number
  workflowInfo: WorkflowInfo
  isWorkflow: boolean
  task: Task
  taskIdWhenWf: number | null
  userRole: string | null
  api: any
  navigate: ReturnType<typeof useNavigate>
  service: any
}

export function checkAccessRedirectionForQC(param: CheckAccessRedirectionParams) {
  const {
    wfStatus,
    currentLoggedInUserId,
    currentTaskAssigneeIds,
    wfInitiatorId,
    wfId,
    workflowInfo,
    isWorkflow,
    task,
    taskIdWhenWf,
    userRole,
    navigate
  } = param
  if (wfStatus === WorkflowStatus.ACTIVE) {
    if (currentTaskAssigneeIds.includes(currentLoggedInUserId as any) || currentLoggedInUserId === wfInitiatorId) {
      getWorkflowFiles(wfId, { pageStart: 0, pageSize: 1 }, true).then((resp: WorkflowFilesResponse) => {
        const firstFile = resp?.elements?.[0]?.draftFsi
        const taskIdParam = !isWorkflow ? task.id : taskIdWhenWf || 0
        navigate(`/show-file-qc/${firstFile?.id}?action=${TASK_ACTIONS.taskComplete}&taskId=${taskIdParam}`, {
          replace: true
        })
      })
    } else {
      toast.warning('GENERATING_VIEWABLE_DOC_NOT_ACCESS_MSG')
    }
  } else if (_.includes([WorkflowStatus.FINISHED, WorkflowStatus.CANCELLED], workflowInfo.status)) {
    const wfParticipant = workflowInfo?.workflowSteps?.reduce((acc: number[], item) => {
      item != null ? acc.push(item?.taskTeamSteps?.[0]?.taskTeamItems?.[0]?.userId as number) : null
      return acc
    }, [])
    if (wfParticipant?.includes(currentLoggedInUserId as any) || ['ADMIN', 'ETMF_MANAGER'].includes(userRole as any)) {
      getWorkflowFiles(wfId, { pageStart: 0, pageSize: 1 }, true).then((resp: WorkflowFilesResponse) => {
        const firstFile = resp?.elements?.[0]?.sourceFsi
        const taskIdParam = !isWorkflow ? task.id : taskIdWhenWf || 0
        const versionParam = firstFile?.versionId ? `&version=${firstFile.versionId}` : ''
        navigate(
          `/show-file-qc/${firstFile?.id}?action=${TASK_ACTIONS.taskComplete}&taskId=${taskIdParam}${versionParam}`
        )
      })
    } else {
      toast.warning('GENERATING_VIEWABLE_DOC_NOT_ACCESS_MSG')
    }
  }
}

function useCheckDocAccessInQC(task: Task, isWorkflow: boolean) {
  const context = useServiceContext()
  const { api, service } = context
  const navigate = useNavigate()

  function checkAccess() {
    const currentLoggedInUserId = service.auth.getUserId()
    const userRole = service.auth.getRole()
    let currentTaskAssigneeIds: (number | undefined)[] = [task?.assignee?.id]
    const wfId = isWorkflow ? task?.id : task?.workflow?.id
    let workflowInfo: WorkflowInfo | null = null
    let taskIdWhenWf: number | null = null
    const promise: [Promise<WorkflowInfo>, Promise<TasksResponse>?] = [api.workflows.getById(wfId as number)]
    isWorkflow ? promise.push(api.tasks.getTasksList({ workflowId: wfId })) : null

    Promise.all(promise).then(([wfResp, taskResp]: [WorkflowInfo, TasksResponse?]) => {
      workflowInfo = wfResp
      const wfInitiatorId = workflowInfo?.initiatorUserId
      const wfStatus = workflowInfo?.status
      !(currentTaskAssigneeIds.length && currentTaskAssigneeIds[0] != null)
        ? (currentTaskAssigneeIds = _.map(
            _.find(workflowInfo?.workflowSteps, { status: WorkflowStatus.ACTIVE })?.taskTeamSteps?.[0]?.taskTeamItems,
            'userId'
          ))
        : null
      if (isWorkflow) {
        const tasks = taskResp?.elements
        const openTask =
          tasks?.find((item) => item?.status === 'OPEN' && task?.assignee?.id === currentLoggedInUserId) || tasks?.[0]
        taskIdWhenWf = openTask?.id as number
      }
      checkAccessRedirectionForQC({
        wfStatus,
        currentLoggedInUserId,
        currentTaskAssigneeIds,
        wfInitiatorId,
        wfId: wfId as number,
        workflowInfo: workflowInfo as WorkflowInfo,
        isWorkflow,
        task,
        taskIdWhenWf,
        userRole,
        api,
        navigate,
        service
      })
    })
  }
  return {
    checkAccess
  }
}

export function parseQCDocStatus(status: string) {
  switch (status) {
    case QC_DOC_STATUS.NA:
      return 'QC_STATUS_NA'
    case QC_DOC_STATUS.PASSED:
      return 'QC_STATUS_PASS'
    case QC_DOC_STATUS.FAILED:
      return 'QC_STATUS_FAIL'
    case QC_DOC_STATUS.REMEDIATED:
      return 'QC_STATUS_REMEDIATED'
  }
}

export default useCheckDocAccessInQC
